// import { EPSON_DEFAULT_PORT, EPSON_DEFAULT_DEVICE_ID } from "../interfaces/epson.interface";
import type { EpsonConnectionConfig } from '../interfaces/epson.interface';
import { useCallback, useState } from 'react';

export const EPSON_DEFAULT_PORT = 8008;
// usually "local_printer" unless you changed it in WebConfig
export const EPSON_DEFAULT_DEVICE_ID = 'Cashier';
export type EpsonPrinter = any;
export type EpsonDevice = any;

/* ============================================
   CENTER ALIGN TEXT
============================================ */
export function centerText(text: string, lineWidth: number) {
  if (!text) return ''.padStart(lineWidth, ' ');
  text = String(text);

  // If string is longer, trim it
  if (text.length >= lineWidth) return text.slice(0, lineWidth);

  const remaining = lineWidth - text.length;
  const left = Math.floor(remaining / 2);
  const right = remaining - left;

  return ' '.repeat(left) + text + ' '.repeat(right);
}

export function separatorLine(length = 40, char = '-') {
  return char.repeat(length);
}

export function separatorEqual(length = 40, char = '=') {
  return char.repeat(length);
}

export function separatorAsterisk(length = 40, char = '*') {
  return char.repeat(length);
}

export function printLine(printer: any, text: string = '') {
  printer.addText(`${text}\n`);
}

// SAFE PRINT LINE WRAPPER
export function safePrintLine(printer: any, text: string | undefined) {
  try {
    printLine(printer, text);
  } catch (e) {
    console.warn('Safe print failed:', e);
  }
}

export function formatColumns(values, weights, lineWidth) {
  if (!Array.isArray(values) || !Array.isArray(weights)) {
    throw new Error('values and weights must be arrays');
  }
  if (values.length !== weights.length) {
    throw new Error('values and weights must have the same length');
  }

  const totalWeight = weights.reduce((a, b) => a + b, 0) || 1;

  // 1) compute integer column widths and correct rounding difference
  let colWidths = weights.map((w) => Math.floor((w / totalWeight) * lineWidth));
  const sumWidths = colWidths.reduce((a, b) => a + b, 0);
  const diff = lineWidth - sumWidths;
  if (diff !== 0) {
    // add remainder to the last column (safe)
    colWidths[colWidths.length - 1] += diff;
  }

  // 2) build the line
  return values
    .map((val, i) => {
      const colWidth = colWidths[i];
      let str = val == null ? '' : String(val);

      // If column width is zero or negative (defensive), return empty string
      if (colWidth <= 0) return '';

      if (i === values.length - 1) {
        // last column: right aligned. If too long, keep the rightmost chars
        if (str.length > colWidth) {
          // take rightmost colWidth characters
          const kept = str.slice(-colWidth);
          return kept.padStart(colWidth, ' ');
        }
        return str.padStart(colWidth, ' ');
      } else {
        // other columns: left aligned. If too long, truncate + ellipsis (if space)
        if (str.length > colWidth) {
          if (colWidth > 1) {
            const truncated = str.slice(0, colWidth - 1) + '…';
            return truncated.padEnd(colWidth, ' ');
          }
          // if only 1 char width, just clip
          return str.slice(0, colWidth);
        }
        return str.padEnd(colWidth, ' ');
      }
    })
    .join('');
}

type ColumnAlignment = 'left' | 'right';

export function formatColumnsWithAlignments(
  values: Array<string | number>,
  weights: number[],
  lineWidth: number,
  alignments: ColumnAlignment[] = [],
): string {
  if (values.length !== weights.length) {
    throw new Error('values and weights must have the same length');
  }

  const totalWeight = weights.reduce((a, b) => a + b, 0) || 1;

  let colWidths = weights.map((w) => Math.floor((w / totalWeight) * lineWidth));

  const diff = lineWidth - colWidths.reduce((a, b) => a + b, 0);
  if (diff !== 0) {
    colWidths[colWidths.length - 1] += diff;
  }

  return values
    .map((val, i) => {
      const colWidth = colWidths[i];
      const align: ColumnAlignment = alignments[i] ?? 'left';
      let str = val == null ? '' : String(val);

      if (colWidth <= 0) return '';

      if (str.length > colWidth) {
        if (colWidth > 1) {
          str = str.slice(0, colWidth - 1) + '…';
        } else {
          str = str.slice(0, colWidth);
        }
      }

      return align === 'right'
        ? str.padStart(colWidth, ' ')
        : str.padEnd(colWidth, ' ');
    })
    .join('');
}

export function formatTwoColumns(colA, colB, lineWidth) {
  const a = String(colA ?? '');
  const b = String(colB ?? '');

  const totalLength = a.length + b.length;
  let spaces = lineWidth - totalLength;

  if (spaces < 1) spaces = 1; // at least one space

  return a + ' '.repeat(spaces) + b;
}

export function formatColumnsRight(values, lineWidth, ratios: number[] = []) {
  // If no ratios are provided, split evenly
  if (!ratios.length) {
    ratios = new Array(values.length).fill(1);
  }

  const totalRatio = ratios.reduce((a, b) => a + b, 0);

  // Calculate widths based on ratios
  const widths = ratios.map((r) => Math.floor((r / totalRatio) * lineWidth));

  // Fix rounding error (ensure total == lineWidth)
  const diff = lineWidth - widths.reduce((a, b) => a + b, 0);
  if (diff > 0) widths[widths.length - 1] += diff;

  // Format values right-aligned per column
  return values
    .map((val, i) => {
      const str = String(val ?? '');
      if (str.length > widths[i]) {
        return str.slice(0, widths[i] - 1) + '…';
      }
      return str.padStart(widths[i], ' ');
    })
    .join('');
}

export function formatUnderline(label, lineWidth) {
  // length of label with space after (e.g. "Name: ")
  const prefix = label + ' ';
  const remaining = lineWidth - prefix.length;

  // make underscores for remaining space
  const underscores = '_'.repeat(Math.max(remaining, 0));

  return prefix + underscores;
}

export function signatureLine(separatorNo = 42) {
  const half = Math.floor(separatorNo / 2);
  return '_'.repeat(half);
}

function getEpson(): any {
  if (!window.epson) {
    throw new Error('Epson ePOS SDK not loaded. Check index.html script tags.');
  }
  return window.epson;
}

/**
 * Connect to Epson TM-T82 (ePOS-enabled) and create a Printer device.
 */
export function connectToEpsonPrinter(
  config: EpsonConnectionConfig,
): Promise<{ device: EpsonDevice; printer: EpsonPrinter }> {
  const {
    ip,
    port = EPSON_DEFAULT_PORT,
    deviceId = EPSON_DEFAULT_DEVICE_ID,
  } = config;

  return new Promise((resolve, reject) => {
    try {
      const epson = getEpson();
      const device = new epson.ePOSDevice();

      device.connect(ip, port, (result: string) => {
        // result is typically "OK" or error code
        if (result !== 'OK' && result !== 'SSL_CONNECT_OK') {
          reject(new Error(`Failed to connect to printer. Result: ${result}`));
          return;
        }

        device.createDevice(
          deviceId,
          device.DEVICE_TYPE_PRINTER,
          { crypto: false, buffer: false },
          (printer: EpsonPrinter | null, code: string) => {
            if (!printer) {
              reject(
                new Error(`Failed to create printer device. Code: ${code}`),
              );
              return;
            }
            // optional: attach receive handler
            printer.onreceive = (_res: any) => {};

            resolve({ device, printer });
          },
        );
      });
    } catch (err) {
      reject(err);
    }
  });
}

export function useEpsonPrinter() {
  const [printer, setPrinter] = useState<EpsonPrinter | null>(null);
  const [device, setDevice] = useState<EpsonDevice | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connect = useCallback(async (config: EpsonConnectionConfig) => {
    setIsConnecting(true);
    setError(null);

    try {
      const { device: dev, printer: p } = await connectToEpsonPrinter(config);
      setDevice(dev);

      setPrinter(p);

      return p; // return printer instance
    } catch (err: any) {
      console.error(err);
      setError(err?.message ?? 'Unknown error');
      setDevice(null);
      setPrinter(null);
      return null;
    } finally {
      setIsConnecting(false);
    }
  }, []);

  const disconnect = useCallback(() => {
    try {
      if (printer) {
        try {
          printer.disconnect && printer.disconnect();
        } catch (e) {
          console.warn('Printer disconnect error:', e);
        }
      }

      if (device) {
        try {
          // Epson API uses .disconnect() to close the TCP session
          device.disconnect && device.disconnect();
        } catch (e) {
          console.warn('Device disconnect error:', e);
        }
      }
    } finally {
      setPrinter(null);
      setDevice(null);
    }
  }, [printer, device]);

  return {
    printer,
    device,
    isConnecting,
    error,
    connect,
    disconnect,
  };
}
